import React, { useState, useMemo } from "react";
import { View, Text, TouchableOpacity, Modal } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import i18n from "../../locales/i18n";

// ─── Inline Calendar Range Picker ───────────────────────────────
const InlineCalendarPicker: React.FC<{
  visible: boolean;
  onClose: () => void;
  onApply: (start: Date, end: Date) => void;
}> = ({ visible, onClose, onApply }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [viewDate, setViewDate] = useState(new Date());
  const [selecting, setSelecting] = useState<"start" | "end">("start");

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  // Shift so Saturday=0
  const shiftedFirst = (firstDayOfWeek + 1) % 7;

  const dayNames = useMemo(
    () => [
      i18n.t("saturday")?.slice(0, 2) || "Sa",
      i18n.t("sunday")?.slice(0, 2) || "Su",
      i18n.t("monday")?.slice(0, 2) || "Mo",
      i18n.t("tuesday")?.slice(0, 2) || "Tu",
      i18n.t("wednesday")?.slice(0, 2) || "We",
      i18n.t("thursday")?.slice(0, 2) || "Th",
      i18n.t("friday")?.slice(0, 2) || "Fr",
    ],
    [],
  );

  const monthNames = [
    "Jan",
    "Fév",
    "Mar",
    "Avr",
    "Mai",
    "Jun",
    "Jul",
    "Aoû",
    "Sep",
    "Oct",
    "Nov",
    "Déc",
  ];

  const days: (number | null)[] = [];
  for (let i = 0; i < shiftedFirst; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const toDateKey = (d: number) =>
    `${year}-${(month + 1).toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;

  const dateToKey = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;

  const isStart = (d: number) =>
    startDate && dateToKey(startDate) === toDateKey(d);
  const isEnd = (d: number) => endDate && dateToKey(endDate) === toDateKey(d);
  const isInRange = (d: number) => {
    if (!startDate || !endDate) return false;
    const key = toDateKey(d);
    return key > dateToKey(startDate) && key < dateToKey(endDate);
  };
  const isToday = (d: number) => {
    const t = new Date();
    return (
      t.getFullYear() === year && t.getMonth() === month && t.getDate() === d
    );
  };

  const handleDayPress = (d: number) => {
    const selected = new Date(year, month, d);
    if (selecting === "start") {
      setStartDate(selected);
      if (endDate && selected >= endDate) setEndDate(null);
      setSelecting("end");
    } else {
      if (startDate && selected <= startDate) {
        setStartDate(selected);
        setEndDate(null);
        setSelecting("end");
      } else {
        setEndDate(selected);
        setSelecting("start");
      }
    }
  };

  const formatDisplay = (d: Date | null) => {
    if (!d) return "--/--/----";
    return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
  };

  const canApply = startDate && endDate;

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View className="flex-1 justify-end bg-black/40">
        <View className="bg-white rounded-t-3xl p-5 pb-8">
          {/* Header */}
          <View className="flex-row items-center justify-between mb-4">
            <Text className="text-xl font-bold text-gray-800">
              {i18n.t("selectDateRange")}
            </Text>
            <TouchableOpacity onPress={onClose}>
              <MaterialIcons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          {/* Date Inputs */}
          <View className="flex-row gap-3 mb-4">
            <TouchableOpacity
              onPress={() => setSelecting("start")}
              className="flex-1 rounded-xl p-3"
              style={{
                backgroundColor: selecting === "start" ? "#E8F4F8" : "#F5F5F5",
                borderWidth: 2,
                borderColor: selecting === "start" ? "#1379A2" : "transparent",
              }}
            >
              <Text className="text-xs text-gray-500 mb-1">
                {i18n.t("startDate")}
              </Text>
              <Text
                className="text-base font-bold"
                style={{ color: startDate ? "#1379A2" : "#CBD5E1" }}
              >
                {formatDisplay(startDate)}
              </Text>
            </TouchableOpacity>

            <View className="justify-center">
              <MaterialIcons name="arrow-forward" size={20} color="#1379A2" />
            </View>

            <TouchableOpacity
              onPress={() => setSelecting("end")}
              className="flex-1 rounded-xl p-3"
              style={{
                backgroundColor: selecting === "end" ? "#E8F4F8" : "#F5F5F5",
                borderWidth: 2,
                borderColor: selecting === "end" ? "#1379A2" : "transparent",
              }}
            >
              <Text className="text-xs text-gray-500 mb-1">
                {i18n.t("endDate")}
              </Text>
              <Text
                className="text-base font-bold"
                style={{ color: endDate ? "#1379A2" : "#CBD5E1" }}
              >
                {formatDisplay(endDate)}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Month Navigation */}
          <View className="flex-row items-center justify-between mb-3 px-1">
            <TouchableOpacity
              onPress={() => setViewDate(new Date(year, month - 1, 1))}
              className="w-9 h-9 rounded-xl bg-gray-100 items-center justify-center"
            >
              <MaterialIcons name="chevron-left" size={22} color="#475569" />
            </TouchableOpacity>
            <Text className="text-base font-bold text-gray-800">
              {monthNames[month]} {year}
            </Text>
            <TouchableOpacity
              onPress={() => setViewDate(new Date(year, month + 1, 1))}
              className="w-9 h-9 rounded-xl bg-gray-100 items-center justify-center"
            >
              <MaterialIcons name="chevron-right" size={22} color="#475569" />
            </TouchableOpacity>
          </View>

          {/* Day Names */}
          <View className="flex-row mb-1">
            {dayNames.map((name, i) => (
              <View key={i} className="flex-1 items-center py-1">
                <Text className="text-xs font-bold text-gray-400 uppercase">
                  {name}
                </Text>
              </View>
            ))}
          </View>

          {/* Day Grid */}
          <View className="flex-row flex-wrap">
            {days.map((day, i) => {
              if (!day) {
                return (
                  <View
                    key={`e-${i}`}
                    style={{ width: "14.28%", height: 40 }}
                  />
                );
              }
              const start = isStart(day);
              const end = isEnd(day);
              const range = isInRange(day);
              const today = isToday(day);
              const selected = start || end;

              return (
                <TouchableOpacity
                  key={`d-${i}`}
                  onPress={() => handleDayPress(day)}
                  style={{
                    width: "14.28%",
                    height: 40,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <View
                    style={{
                      position: "absolute",
                      top: 4,
                      bottom: 4,
                      left: start ? "50%" : 0,
                      right: end ? "50%" : 0,
                      backgroundColor:
                        range || (start && endDate) || (end && startDate)
                          ? "#E0F2FE"
                          : "transparent",
                    }}
                  />
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: selected ? 12 : 10,
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: selected
                        ? "#1379A2"
                        : range
                          ? "#E0F2FE"
                          : today
                            ? "#F0F9FF"
                            : "transparent",
                      zIndex: 1,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: selected ? "800" : today ? "700" : "500",
                        color: selected
                          ? "#fff"
                          : today
                            ? "#1379A2"
                            : range
                              ? "#0C4A6E"
                              : "#334155",
                      }}
                    >
                      {day}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Apply Button */}
          <TouchableOpacity
            onPress={() => {
              if (startDate && endDate) {
                onApply(startDate, endDate);
              }
            }}
            disabled={!canApply}
            className="mt-4 rounded-xl py-3 items-center"
            style={{
              backgroundColor: canApply ? "#1379A2" : "#E2E8F0",
            }}
          >
            <Text
              className="text-base font-bold"
              style={{ color: canApply ? "#fff" : "#94A3B8" }}
            >
              {i18n.t("searchByDate")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default InlineCalendarPicker;
